/**
 * 手机 App 里的上课音频会话：进教室开、离开关、被打断时静音、结束后恢复。
 *
 * 为什么打断期间要主动静音：来电时系统把麦克风给了电话，WebRTC 那条轨还在 enabled 状态，
 * 于是「课堂里显示我在说话、实际大家听不到」；更糟的是有些机型会把通话声音混进去。
 * 所以打断一来就 disable 本地音轨，结束后恢复到用户原本的状态（他可能本来就是静音的）。
 */

import { useEffect, useRef, useState } from 'react'

import {
  audioSessionState,
  onInterruptionChange,
  startAudioSession,
  stopAudioSession,
  type AudioSessionState,
} from './audioSession'
import { isMobileApp } from './desktop'
import type { MediaEngine } from './media'

const POLL_MS = 2000

export function useAudioSession(
  media: MediaEngine | null | undefined,
  roomName: string,
  micOn: boolean,
): { interrupted: boolean; route: string } {
  const [interrupted, setInterrupted] = useState(false)
  const [route, setRoute] = useState('')
  // 打断前用户自己的静音状态：恢复时要还原成这个，而不是一律取消静音。
  const beforeRef = useRef<boolean | null>(null)
  const mediaRef = useRef(media)
  mediaRef.current = media

  useEffect(() => {
    if (!isMobileApp()) return
    let alive = true
    void startAudioSession(roomName || '网梯直播课堂')

    // 原生侧的打断是系统通知，没有回调通道时用轮询问状态 —— 两秒一次，代价可以忽略，
    // 而漏掉一次「打断结束」的代价是这节课再也听不见声音。
    const timer = window.setInterval(() => {
      void audioSessionState().then((s: AudioSessionState) => {
        if (!alive) return
        setRoute(s.route)
        setInterrupted((prev) => {
          const m = mediaRef.current
          const act = onInterruptionChange(prev, s.interrupted, m?.isMuted() ?? false, beforeRef.current)
          if (!act) return prev
          m?.setMuted(act.muted)
          beforeRef.current = act.remember
          return s.interrupted
        })
      })
    }, POLL_MS)

    return () => {
      alive = false
      window.clearInterval(timer)
      void stopAudioSession()
    }
    // roomName 变化（换到分组子房间）时不重开会话：同一个音频会话继续用就行。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 用户自己开关麦克风时，把打断期间记住的「原状态」同步过去，
  // 否则挂断电话后会被恢复成他早就改掉的旧状态。
  useEffect(() => {
    if (beforeRef.current !== null) beforeRef.current = !micOn ? true : beforeRef.current
  }, [micOn])

  return { interrupted, route }
}
